/* Global feedback overlay — replaces sonner toast.success / toast.error.

   Behavior:
     • Success (yes.png): green ✓ image fades in centered, message printed
       below it, auto-dismiss after 2.5 s.
     • Error   (no.png):  red  ✗ image fades in centered, message below;
       same 2.5 s auto-dismiss so the user can correct the mistake without
       an extra tap. (Earlier iteration required a tap; reverted per UX
       feedback — sub-3 s dwell is enough to register without blocking.)

   The overlay sits at z-[9999], pointer-events scoped to the inner
   card so it can be tap-dismissed without blocking the rest of the UI.

   Designed to look identical on desktop and mobile — the image scales
   responsively, message text wraps cleanly up to ~32 ch.
*/
import { useEffect, useState, useRef } from "react";
import feedbackBus from "@/lib/feedbackBus";

const AUTO_MS = 2500;

export default function FeedbackOverlay() {
  // Single active event at a time. New events replace the previous one
  // (mirrors how toast collapses rapid duplicates).
  const [evt, setEvt] = useState(null);
  // Fade-out animation state — true once the dismiss timer (or click) fires.
  const [closing, setClosing] = useState(false);
  const timerRef = useRef(null);
  const fadeRef = useRef(null);

  useEffect(() => {
    return feedbackBus.subscribe((next) => {
      // A brand-new event mid-fade should cancel the pending unmount so we
      // don't null-out the freshly-set state.
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (fadeRef.current) { clearTimeout(fadeRef.current); fadeRef.current = null; }
      // Subtle haptic feedback on supporting devices (Android Chrome, most
      // Android browsers; iOS Safari ignores). Wrapped in a try/catch since
      // some hardened browsers throw on `navigator.vibrate` outside a user
      // gesture even though most modern browsers tolerate it.
      try {
        if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
          navigator.vibrate(next.kind === "success" ? 60 : [40, 80, 40]);
        }
      } catch { /* haptics are best-effort */ }
      setClosing(false);
      setEvt(next);
    });
  }, []);

  useEffect(() => {
    if (!evt || closing) return undefined;
    // Both success AND error now auto-dismiss after AUTO_MS so the user
    // can immediately return to the previous screen and fix the issue.
    timerRef.current = setTimeout(() => {
      setClosing(true);
      // Wait for the CSS fade-out (200 ms) before unmounting.
      fadeRef.current = setTimeout(() => setEvt(null), 220);
    }, AUTO_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [evt, closing]);

  if (!evt) return null;

  const handleDismiss = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (fadeRef.current) { clearTimeout(fadeRef.current); fadeRef.current = null; }
    setClosing(true);
    fadeRef.current = setTimeout(() => setEvt(null), 220);
  };

  const isSuccess = evt.kind === "success";
  const img = isSuccess ? "/yes.png" : "/no.png";
  const accent = isSuccess ? "text-emerald-700" : "text-red-700";
  const ring = isSuccess ? "ring-emerald-300/60" : "ring-red-300/60";

  return (
    <div
      data-testid={`feedback-overlay-${evt.kind}`}
      aria-live="polite"
      role={isSuccess ? "status" : "alert"}
      onClick={handleDismiss}
      className={`fixed inset-0 z-[9999] flex items-center justify-center px-4
                  bg-black/15 backdrop-blur-[2px]
                  ${closing ? "animate-fb-out" : "animate-fb-in"}`}
      style={{ cursor: "pointer" }}
    >
      <div className={`pointer-events-none flex flex-col items-center gap-3
                       p-6 rounded-2xl bg-white/90 shadow-2xl ring-2 ${ring}
                       max-w-[min(420px,90vw)]`}
      >
        <img
          src={img}
          alt={isSuccess ? "OK" : "Σφάλμα"}
          draggable="false"
          className={`select-none w-40 h-40 sm:w-48 sm:h-48 object-contain
                      drop-shadow-[0_4px_18px_rgba(0,0,0,0.18)]`}
        />
        {evt.message && (
          <div className={`text-center text-base font-bold leading-snug ${accent}`}
               data-testid="feedback-overlay-message">
            {evt.message}
          </div>
        )}
        {!isSuccess && (
          <div className="text-xs text-slate-500">αυτόματο κλείσιμο…</div>
        )}
      </div>
    </div>
  );
}
